import { WebEventPayload } from "../core/types";
import { HttpTransport, TransportBatchPayload } from "./http";
import { BeaconTransport } from "./beacon";

export interface BatcherOptions {
  communityId: string;
  apiKey: string;
  endpoint: string;
  batchSize?: number;
  flushIntervalMs?: number;
  sdkVersion?: string;
  debug?: boolean;
}

export class EventBatcher {
  private queue: WebEventPayload[] = [];
  private communityId: string;
  private apiKey: string;
  private endpoint: string;
  private batchSize: number;
  private flushIntervalMs: number;
  private sdkVersion: string;
  private debug: boolean;
  private httpTransport: HttpTransport;
  private flushTimer: any = null;
  private isFlushing: boolean = false;
  private retryAttempts: number = 0;
  private maxRetries: number = 3;

  constructor(options: BatcherOptions) {
    this.communityId = options.communityId;
    this.apiKey = options.apiKey;
    this.endpoint = options.endpoint.replace(/\/$/, "");
    this.batchSize = options.batchSize || 20;
    this.flushIntervalMs = options.flushIntervalMs || 5000;
    this.sdkVersion = options.sdkVersion || "1.0.0";
    this.debug = options.debug || false;

    this.httpTransport = new HttpTransport(this.endpoint, this.apiKey, this.debug);

    this.startFlushTimer();
    this.setupUnloadListeners();
  }

  public enqueue(event: WebEventPayload, immediate: boolean = false): void {
    this.queue.push(event);

    if (this.debug) {
      console.log(`[Thrico Web SDK] Enqueued event: ${event.eventName} (Queue size: ${this.queue.length})`);
    }

    if (immediate || this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  public async flush(): Promise<void> {
    if (this.queue.length === 0 || this.isFlushing) return;

    this.isFlushing = true;
    const batch = this.queue.splice(0, this.batchSize);

    const payload: TransportBatchPayload = {
      communityId: this.communityId,
      sentAt: new Date().toISOString(),
      sdkVersion: this.sdkVersion,
      events: batch,
    };

    const success = await this.httpTransport.sendBatch(payload);

    if (!success) {
      // Requeue failed items for retry if under retry limit
      if (this.retryAttempts < this.maxRetries) {
        this.retryAttempts++;
        const backoffMs = Math.pow(2, this.retryAttempts) * 1000;
        this.queue.unshift(...batch);

        if (this.debug) {
          console.warn(`[Thrico Web SDK] Batch delivery failed. Retrying in ${backoffMs}ms (Attempt ${this.retryAttempts})`);
        }

        setTimeout(() => {
          this.flush();
        }, backoffMs);
      } else {
        if (this.debug) {
          console.error(`[Thrico Web SDK] Dropped ${batch.length} event(s) after ${this.maxRetries} failed retries.`);
        }
        this.retryAttempts = 0;
      }
    } else {
      this.retryAttempts = 0;
    }

    this.isFlushing = false;

    // Continue flushing if queue still exceeds batch size
    if (this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   * Synchronous / beacon flush for page exit
   */
  public flushSync(): void {
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.queue.length);
    const payload: TransportBatchPayload = {
      communityId: this.communityId,
      sentAt: new Date().toISOString(),
      sdkVersion: this.sdkVersion,
      events: batch,
    };

    BeaconTransport.send(this.endpoint, this.apiKey, payload);
  }

  private startFlushTimer(): void {
    if (typeof window === "undefined") return;
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.flushIntervalMs);
  }

  private setupUnloadListeners(): void {
    if (typeof window === "undefined" || typeof window.addEventListener !== "function") return;

    const handleExit = () => {
      this.flushSync();
    };

    window.addEventListener("pagehide", handleExit);
    window.addEventListener("beforeunload", handleExit);

    if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          this.flushSync();
        }
      });
    }
  }

  public destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flushSync();
  }
}
